// ============================================================================
// ClientRoster — a scannable roster with two lenses:
//   • Needs follow-up — active clients with no upcoming session, stale
//   • Favorites — starred clients
// Each row shows avatar, name, last-session recency, trend, and a star toggle.
// Data comes from the live dashboard context; the star button hits the real
// optimistic star mutation so it persists + syncs across devices.
// ============================================================================
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Avatar, Badge, Card, CardHeader, EmptyState, Tabs } from "../components";
import { IconArrowDown, IconArrowUp, IconMinus, IconStar } from "../components/icons";
import { useStarClient } from "../hooks/clients";
import { useDashboardCtx } from "./DashboardDataContext";
import type { RosterEntryVM, Trend } from "./useDashboardData";
import { cn } from "../lib/cn";

type Lens = "follow-up" | "favorites" | "all";

export function ClientRoster() {
  const { t } = useTranslation();
  const { roster } = useDashboardCtx();
  const [lens, setLens] = useState<Lens>("follow-up");

  const { followUp, favs, all } = useMemo(() => {
    const followUpEntries = roster
      .filter((e) => e.needsFollowUp)
      .sort((a, b) => (b.daysSinceLast ?? 999) - (a.daysSinceLast ?? 999));
    return {
      followUp: followUpEntries,
      favs: roster.filter((e) => e.client.starred),
      all: roster,
    };
  }, [roster]);

  const entries = lens === "follow-up" ? followUp : lens === "favorites" ? favs : all;

  return (
    <Card className="flex h-full flex-col">
      <CardHeader title={t("roster.title")} subtitle={t("roster.subtitle")} />

      <Tabs
        className="mb-4 self-start"
        value={lens}
        onChange={(id) => setLens(id as Lens)}
        items={[
          { id: "follow-up", label: t("roster.followUp"), badge: <Count n={followUp.length} active={lens === "follow-up"} /> },
          { id: "favorites", label: t("roster.favorites"), badge: <Count n={favs.length} active={lens === "favorites"} /> },
          { id: "all", label: t("roster.all"), badge: <Count n={all.length} active={lens === "all"} /> },
        ]}
      />

      {entries.length === 0 ? (
        <EmptyState
          title={lens === "follow-up" ? t("roster.allCaughtUp") : t("roster.nothingYet")}
          description={
            lens === "follow-up"
              ? t("roster.allCaughtUpBlurb")
              : t("roster.nothingYetBlurb")
          }
          className="flex-1 !py-10"
        />
      ) : (
        <ul className="-mr-1 flex-1 space-y-1.5 overflow-y-auto pr-1">
          {entries.map((e) => (
            <RosterRow key={e.client.id} entry={e} />
          ))}
        </ul>
      )}
    </Card>
  );
}

function Count({ n, active }: { n: number; active: boolean }) {
  return (
    <span
      className={cn(
        "grid h-5 min-w-[1.25rem] place-items-center rounded-pill px-1.5 text-[0.68rem] font-extrabold tabular-nums",
        active ? "bg-white/25 text-white" : "bg-surface text-body-soft ring-1 ring-line",
      )}
    >
      {n}
    </span>
  );
}

const TREND_META: Record<Trend, { icon: typeof IconArrowUp; tone: string }> = {
  down: { icon: IconArrowDown, tone: "text-lime-600" }, // weight down = good
  up: { icon: IconArrowUp, tone: "text-volt-600" },
  flat: { icon: IconMinus, tone: "text-body-soft" },
};

function RosterRow({ entry }: { entry: RosterEntryVM }) {
  const { t } = useTranslation();
  const { client, daysSinceLast, needsFollowUp: needs } = entry;
  const starMutation = useStarClient();
  const trend = TREND_META[client.trend];
  const TrendIcon = trend.icon;

  const lastLabel =
    daysSinceLast === undefined
      ? t("roster.noSessionsYet")
      : daysSinceLast === 0
        ? t("roster.trainedToday")
        : t("roster.dayAgo", { count: daysSinceLast });

  return (
    <li
      className={cn(
        "flex items-center gap-3 rounded-md p-2.5 ring-1 transition-all duration-fast hover:-translate-y-0.5 hover:shadow-sm",
        needs ? "bg-coral-soft/40 ring-coral/20" : "bg-surface ring-line",
      )}
    >
      <Avatar name={client.name} size="md" status={needs ? "alert" : client.starred ? "online" : null} />

      <Link to={`/app/clients/${client.id}`} className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate font-display text-sm font-bold text-body">{client.name}</span>
          {needs && <Badge tone="danger" className="shrink-0">{t("roster.followUpBadge")}</Badge>}
        </div>
        <div className="mt-0.5 flex items-center gap-2 text-xs text-body-soft">
          <span className={cn("truncate", needs && daysSinceLast && daysSinceLast > 10 && "font-bold text-volt-600")}>
            {lastLabel}
          </span>
          {client.trendLabel && (
            <>
              <span className="text-body-faint">·</span>
              <span className={cn("inline-flex items-center gap-0.5 font-bold tabular-nums", trend.tone)}>
                <TrendIcon width={13} height={13} />
                {client.trendLabel}
              </span>
            </>
          )}
        </div>
      </Link>

      <button
        type="button"
        onClick={() => starMutation.mutate(client.id)}
        disabled={starMutation.isPending}
        aria-label={client.starred ? t("dashboard:roster.unstar", { name: client.name }) : t("dashboard:roster.star", { name: client.name })}
        aria-pressed={client.starred}
        className={cn(
          "grid h-9 w-9 shrink-0 place-items-center rounded-pill transition-colors",
          client.starred ? "text-amber" : "text-body-faint hover:text-amber",
        )}
      >
        <IconStar filled={client.starred} />
      </button>
    </li>
  );
}
